/**
 * PDP mounting uses the existing app tag via the shared mnt_* adapter.
 * Nothing is published until analytics consent is explicitly true. Choices and
 * validation errors made before consent are never buffered or replayed.
 * The payload contains no sole length, URL, cart token or customer data.
 */
import { ANALYTICS_READY_EVENT, sendCustomEvent } from '#existing-tag-analytics';

export const PDP_MOUNTING_EVENT = 'mounting_panel';
export const PDP_MOUNTING_ACTIONS = Object.freeze(['shown', 'choice', 'length_invalid']);
const CHOICES = new Set(['mounted', 'pending', 'unmounted']);
const REASONS = new Set(['empty', 'range', 'format']);
const PANEL_SELECTOR = 'product-info[id^="MainProduct"][data-update-url="true"] pdp-mounting';
const installed = new WeakMap();

export function pdpMountingPayload(action, panel, extra = {}) {
  if (!PDP_MOUNTING_ACTIONS.includes(action)) return null;
  const choice = panel.querySelector('[data-mounting-choice]:checked')?.dataset.mountingChoice;
  const payload = { action, surface: 'pdp', role: 'ski_with_binding' };
  if (CHOICES.has(choice)) payload.saved_choice = choice;
  const input = panel.querySelector('[data-bsl-input]');
  const variant = input?.form?.querySelector('input[name="id"]')?.value;
  if (/^\d+$/.test(variant || '')) payload.variant_id = variant;
  if (action === 'choice') {
    if (!CHOICES.has(choice)) return null;
    payload.choice = choice;
    payload.by = 'customer';
  }
  if (action === 'length_invalid') {
    if (!REASONS.has(extra.reason)) return null;
    payload.error_reason = extra.reason;
  }
  return payload;
}

export function publishPdpMountingEvent(payload, view = globalThis.window) {
  return sendCustomEvent(PDP_MOUNTING_EVENT, payload, { deferUntilConsent: false }, view);
}

export function installPdpMountingAnalytics(doc = globalThis.document, getShopify = () => doc.defaultView.Shopify) {
  if (!doc?.body) return () => {};
  if (installed.has(doc)) return installed.get(doc);
  const view = doc.defaultView;
  const records = new Map();
  const shown = new Set();
  let stopped = false;

  function eligible(panel) {
    return panel?.matches?.(PANEL_SELECTOR) && panel.isConnected;
  }

  function send(panel, action, extra) {
    try {
      if (!eligible(panel)) return false;
      return publishPdpMountingEvent(pdpMountingPayload(action, panel, extra), view);
    } catch {
      return false;
    }
  }

  function maybeShown(panel, record) {
    if (!record.visible || shown.has(record.key) || doc.visibilityState === 'hidden' ||
        panel.closest('[hidden], [aria-hidden="true"], [inert]')) return;
    const style = view.getComputedStyle(record.anchor);
    if (style.display === 'none' || style.visibility === 'hidden') return;
    if (send(panel, 'shown')) shown.add(record.key);
  }

  // A tall panel can exceed the viewport. Its heading is the stable, visible
  // entry point, instead of requiring half of the entire form to fit on screen.
  const observer = typeof view.IntersectionObserver === 'function' ? new view.IntersectionObserver((entries) => {
    for (const entry of entries) {
      for (const [panel, record] of records) {
        if (record.anchor !== entry.target) continue;
        record.visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        maybeShown(panel, record);
      }
    }
  }, { threshold: 0.5 }) : null;

  function scan() {
    if (stopped) return;
    for (const [panel, record] of records) {
      if (!eligible(panel)) {
        observer?.unobserve(record.anchor);
        records.delete(panel);
      }
    }
    for (const panel of doc.querySelectorAll(PANEL_SELECTOR)) {
      if (records.has(panel)) continue;
      const host = panel.closest('product-info');
      const formId = panel.querySelector('[data-bsl-input]')?.getAttribute('form') || '';
      const record = {
        anchor: panel.querySelector('.pdp-mounting__header') || panel,
        key: `${host.dataset.productId || host.id}:${formId}`,
        visible: false,
      };
      records.set(panel, record);
      observer?.observe(record.anchor);
    }
  }

  function consentChanged() {
    if (stopped) return;
    scan();
    for (const [panel, record] of records) maybeShown(panel, record);
  }

  function choiceChanged(event) {
    if (!event.target.matches?.('[data-mounting-choice]')) return;
    send(event.target.closest('pdp-mounting'), 'choice');
  }

  function validationBlocked(event) {
    send(event.target.closest?.('pdp-mounting'), 'length_invalid', { reason: event.detail?.reason });
  }

  const mutations = new view.MutationObserver((changes) => {
    const panelChanged = changes.some((change) => [...change.addedNodes, ...change.removedNodes].some((node) =>
      node.nodeType === 1 && (node.matches('pdp-mounting, product-info') || node.querySelector('pdp-mounting, product-info'))));
    if (panelChanged) scan();
  });
  mutations.observe(doc.body, { childList: true, subtree: true });
  doc.addEventListener('change', choiceChanged);
  doc.addEventListener('pdp-mounting:blocked', validationBlocked);
  doc.addEventListener('visitorConsentCollected', consentChanged);
  doc.addEventListener('consentTrackingApiLoaded', consentChanged);
  doc.addEventListener(ANALYTICS_READY_EVENT, consentChanged);
  doc.addEventListener('visibilitychange', consentChanged);
  scan();

  // Loading the API does not change a consent choice. Its callback also covers
  // an already-consented visitor whose privacy API arrives after this module.
  try {
    getShopify()?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error) consentChanged();
    });
  } catch {}

  const cleanup = () => {
    stopped = true;
    observer?.disconnect();
    mutations.disconnect();
    doc.removeEventListener('change', choiceChanged);
    doc.removeEventListener('pdp-mounting:blocked', validationBlocked);
    doc.removeEventListener('visitorConsentCollected', consentChanged);
    doc.removeEventListener('consentTrackingApiLoaded', consentChanged);
    doc.removeEventListener(ANALYTICS_READY_EVENT, consentChanged);
    doc.removeEventListener('visibilitychange', consentChanged);
    installed.delete(doc);
  };
  installed.set(doc, cleanup);
  return cleanup;
}

if (typeof document !== 'undefined') {
  if (document.body) installPdpMountingAnalytics();
  else document.addEventListener('DOMContentLoaded', () => installPdpMountingAnalytics(), { once: true });
}
